import { Treemap, ResponsiveContainer, Tooltip } from 'recharts'

interface TreemapComponentProps {
  data: Array<Record<string, any>>
  nameKey: string
  valueKey: string
  title?: string
  colors?: string[]
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#14b8a6', // Teal
  '#ec4899', // Rosa
  '#f97316', // Naranja oscuro
]

export function TreemapComponent({
  data,
  nameKey,
  valueKey,
  title,
  colors = DEFAULT_COLORS,
  loading = false,
}: TreemapComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Transformar datos para Treemap
  const treemapData = data.map((item, index) => ({
    name: String(item[nameKey] || 'Sin nombre'),
    size: Number(item[valueKey]) || 0,
    fill: colors[index % colors.length],
  }))

  // Filtrar valores negativos o cero
  const validData = treemapData.filter((item) => item.size > 0)
  

  if (validData.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay valores válidos para mostrar (todos son cero o negativos)
        </div>
      </div>
    )
  }

  // Calcular total
  const total = validData.reduce((sum, item) => sum + item.size, 0)

  // Custom content para las celdas del treemap
  const CustomizedContent = (props: any) => {
    const { x, y, width, height, name, size, fill } = props

    
    // No renderizar si el área es muy pequeña o si size es undefined
    //if (width < 30 || height < 30 || size === undefined || size === null) return null
     

    //const percentage = ((size / total) * 100).toFixed(1)
    const showText = width > 60 && height > 40
    const percentage = ((size / total) * 100).toFixed(1)

    return (
    <g>
      {/* RECT SIEMPRE */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="#fff"
        strokeWidth={1}
      />

      {/* TEXTO SOLO SI HAY ESPACIO */}
      {showText && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 8}
            textAnchor="middle"
            fill="#fff"
            fontSize={9}
            
          >
           {name?.length * 9 > width * 0.9 
    ? `${name.substring(0, Math.floor(width / 9) - 3)}...` 
    : name || ''}
          </text>

          <text
            x={x + width / 2}
            y={y + height / 2 + 8}
            textAnchor="middle"
            fill="#fff"
            fontSize={10}
            opacity={0.8}
          >
            {percentage}%
          </text>
        </>
      )}
    </g>
  )
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload

      // Verificar que size existe antes de usarlo
      if (!data || data.size === undefined || data.size === null) return null

      const percentage = ((data.size / total) * 100).toFixed(2)

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">
            Valor: <span className="font-medium">{data.size.toLocaleString('es-ES')}</span>
          </p>
          <p className="text-sm text-gray-600">
            Porcentaje: <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={600}>
        <Treemap
          data={validData}
          dataKey="size"
          aspectRatio={4 / 3}
          stroke="#fff"
          fill="#8884d8"
          content={<CustomizedContent />}
        >
          <Tooltip content={<CustomTooltip />} />
        </Treemap>
      </ResponsiveContainer>

      {/* Info total */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-600">
          Total: <span className="font-semibold text-gray-900">{total.toLocaleString('es-ES')}</span>
          {' '}({validData.length} categorías)
        </p>
      </div>
    </div>
  )
}
